/**
 * Phase 3 — OptimisationNewPage
 *
 * Single-column scrollable composer that stacks:
 *   BaselineScenarioPicker → ObjectiveSelector → DesignVariableSelector
 *   → ConstraintEditor → ComputeProfileSelector → StudyValidationPanel
 *   → Run button.
 *
 * Supports querystring prefill: ?baseline_scenario_id=<id> (used by
 * the Workbench tab's "Start a study" CTA).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Crosshair, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import PageHeader from "@/components/PageHeader";
import optimisation from "@/api/optimisation";
import BaselineScenarioPicker from "@/components/optimisation/BaselineScenarioPicker";
import ObjectiveSelector from "@/components/optimisation/ObjectiveSelector";
import DesignVariableSelector from "@/components/optimisation/DesignVariableSelector";
import ConstraintEditor from "@/components/optimisation/ConstraintEditor";
import ComputeProfileSelector from "@/components/optimisation/ComputeProfileSelector";
import StudyValidationPanel from "@/components/optimisation/StudyValidationPanel";

const Section = ({ index, title, children, helper }) => (
    <section className="space-y-2 border-b border-ux-border-1 pb-6 last:border-b-0">
        <div className="flex items-baseline gap-2">
            <span className="text-[11px] uppercase tracking-wider text-ux-text-3 font-mono">
                {String(index).padStart(2, "0")}
            </span>
            <h2 className="text-[14px] font-medium text-ux-text-1">{title}</h2>
        </div>
        {helper && <p className="text-[11px] text-ux-text-3">{helper}</p>}
        <div className="pt-1">{children}</div>
    </section>
);

export default function OptimisationNewPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefillScenarioId = searchParams.get("baseline_scenario_id") || "";

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [baselineScenarioId, setBaselineScenarioId] = useState(prefillScenarioId);
    const [objective, setObjective] = useState({ type: "maximise_pk" });
    const [registry, setRegistry] = useState([]);
    const [registryErr, setRegistryErr] = useState(null);
    const [selectedVars, setSelectedVars] = useState({});
    const [constraints, setConstraints] = useState({
        range_constraints: [],
        relational_constraints: [],
        cost_constraints: [],
        max_design_cost: 0.55,
        confidence_floor: "provisional",
    });
    const [profile, setProfile] = useState("balanced");
    const [seedsPerCandidate, setSeedsPerCandidate] = useState(4);
    const [dynamicsBackend, setDynamicsBackend] = useState("kinematic");
    const [validation, setValidation] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await optimisation.listVariables();
                if (!cancelled) setRegistry(Array.isArray(list) ? list : []);
            } catch (e) {
                if (!cancelled) {
                    setRegistryErr(
                        formatApiError(e, "Failed to load variables"),
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const requestBody = useMemo(() => {
        const vars = Object.values(selectedVars).map((v) => {
            const out = { path: v.path };
            if (typeof v.min === "number") out.min = v.min;
            if (typeof v.max === "number") out.max = v.max;
            if (typeof v.step === "number") out.step = v.step;
            if (typeof v.cost_weight === "number") out.cost_weight = v.cost_weight;
            return out;
        });
        return {
            name: name || "Untitled design optimisation study",
            description,
            baseline: baselineScenarioId
                ? { source: "scenario_id", scenario_id: baselineScenarioId }
                : null,
            objective,
            variables: vars,
            constraints,
            profile,
            seeds_per_candidate: seedsPerCandidate,
            dynamics_backend: dynamicsBackend,
        };
    }, [
        name, description, baselineScenarioId, objective, selectedVars,
        constraints, profile, seedsPerCandidate, dynamicsBackend,
    ]);

    const blocking = (validation?.errors || []).length;
    const ready =
        baselineScenarioId &&
        Object.keys(selectedVars).length > 0 &&
        validation?.valid &&
        blocking === 0;

    const submit = async () => {
        if (!ready) return;
        setSubmitting(true);
        try {
            const created = await optimisation.createStudy({
                name: name || "Untitled design optimisation study",
                description,
                baseline: { source: "scenario_id", scenario_id: baselineScenarioId },
                objective,
                variables: requestBody.variables,
                constraints,
                profile,
                seeds_per_candidate: seedsPerCandidate,
                dynamics_backend: dynamicsBackend,
            });
            toast.success("Study queued. Tracking live progress…");
            navigate(`/run/optimisation/${created.id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(
                (typeof detail === "string" && detail) ||
                    detail?.message ||
                    e?.message ||
                    "Failed to create study",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-4 pb-12" data-testid="optimisation-new-page">
            <PageHeader
                icon={Crosshair}
                title="New Optimisation Study"
                description="Compose a design-space search against a baseline scenario."
                actions={
                    <Button variant="ghost" onClick={() => navigate("/run/optimisation")}>
                        <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                        Back to studies
                    </Button>
                }
            />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                {/* Left: composer */}
                <div className="xl:col-span-2 space-y-6">
                    <Section index={1} title="Identity & baseline">
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label htmlFor="study-name">Study name</Label>
                                <Input
                                    id="study-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="bg-ux-bg-1"
                                    placeholder="e.g. Payload × climb × Pk pass 1"
                                    data-testid="study-name-input"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="study-description">
                                    Description (optional)
                                </Label>
                                <Textarea
                                    id="study-description"
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="bg-ux-bg-1 text-[12px]"
                                />
                            </div>
                            <BaselineScenarioPicker
                                value={baselineScenarioId}
                                onChange={setBaselineScenarioId}
                            />
                        </div>
                    </Section>

                    <Section
                        index={2}
                        title="Objective"
                        helper="The metric the study ranks candidates against."
                    >
                        <ObjectiveSelector value={objective} onChange={setObjective} />
                    </Section>

                    <Section
                        index={3}
                        title="Design variables"
                        helper="Pick the levers the study will sweep. Edit each band; the registry default is pre-filled."
                    >
                        {registryErr && (
                            <p className="text-[12px] text-ux-danger" data-testid="registry-error">
                                {registryErr}
                            </p>
                        )}
                        <DesignVariableSelector
                            registry={registry}
                            selected={selectedVars}
                            onChange={setSelectedVars}
                        />
                    </Section>

                    <Section
                        index={4}
                        title="Constraints"
                        helper="Range, relational, cost-budget and confidence floor."
                    >
                        <ConstraintEditor
                            constraints={constraints}
                            onChange={setConstraints}
                            selectedVariables={selectedVars}
                        />
                    </Section>

                    <Section
                        index={5}
                        title="Compute profile"
                        helper="Larger profiles produce more candidates and finer Pareto coverage at the cost of wall-clock."
                    >
                        <div className="space-y-3">
                            <ComputeProfileSelector
                                value={profile}
                                onChange={setProfile}
                                estimate={validation}
                            />
                            <div className="grid grid-cols-2 gap-3 max-w-[480px]">
                                <div className="space-y-1">
                                    <Label htmlFor="seeds-per-candidate">
                                        Seeds per candidate
                                    </Label>
                                    <Input
                                        id="seeds-per-candidate"
                                        type="number"
                                        min={1}
                                        max={64}
                                        value={seedsPerCandidate}
                                        onChange={(e) =>
                                            setSeedsPerCandidate(parseInt(e.target.value) || 4)
                                        }
                                        className="bg-ux-bg-1 tabular-nums"
                                        data-testid="seeds-input"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="dynamics-backend">Dynamics backend</Label>
                                    <Select
                                        value={dynamicsBackend}
                                        onValueChange={setDynamicsBackend}
                                    >
                                        <SelectTrigger
                                            id="dynamics-backend"
                                            className="bg-ux-bg-1"
                                            data-testid="dynamics-backend-trigger"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="kinematic">Kinematic</SelectItem>
                                            <SelectItem value="jsbsim_hybrid">
                                                JSBSim hybrid (approximate)
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </Section>
                </div>

                {/* Right: validation + run button */}
                <aside className="space-y-4 xl:sticky xl:top-4 self-start">
                    <StudyValidationPanel
                        body={requestBody}
                        onResult={setValidation}
                    />
                    <Button
                        onClick={submit}
                        disabled={!ready || submitting}
                        className="w-full"
                        size="lg"
                        data-testid="optimisation-run-button"
                    >
                        <Rocket className="w-4 h-4 mr-2" />
                        {submitting ? "Submitting…" : "Run design optimisation"}
                    </Button>
                </aside>
            </div>
        </div>
    );
}
